'use client';

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Coordinates playback across every video mounted with this hook so that only one plays at a time.
 * State is module-scoped because the videos are siblings in a grid with no shared parent state.
 */
const videoRefs = new Map<string, React.RefObject<HTMLVideoElement | null>>();
const subscribers = new Set<() => void>();
let playingVideoId: string | null = null;

function subscribe(onStoreChange: () => void): () => void {
    subscribers.add(onStoreChange);
    return () => {
        subscribers.delete(onStoreChange);
    };
}

function setPlayingVideoId(videoId: string | null): void {
    if (playingVideoId === videoId) return;
    playingVideoId = videoId;
    subscribers.forEach((notify) => notify());
}

function prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

export interface MultiVideoPlayback {
    isPlaying: boolean;
    play: () => Promise<void>;
    pause: () => void;
    reset: () => void;
}

export function useMultiVideoPlayback(
    videoId: string,
    videoRef: React.RefObject<HTMLVideoElement | null>,
): MultiVideoPlayback {
    const isPlayPendingRef = useRef(false);
    const isPauseDeferredRef = useRef(false);

    const isPlaying = useSyncExternalStore(
        subscribe,
        () => playingVideoId === videoId,
        () => false,
    );

    useEffect(() => {
        videoRefs.set(videoId, videoRef);
        return () => {
            videoRefs.delete(videoId);
            if (playingVideoId === videoId) setPlayingVideoId(null);
        };
    }, [videoId, videoRef]);

    const pauseNow = useCallback(() => {
        videoRef.current?.pause();
        if (playingVideoId === videoId) setPlayingVideoId(null);
    }, [videoId, videoRef]);

    const play = useCallback(async () => {
        const video = videoRef.current;
        if (!video || prefersReducedMotion()) return;

        if (playingVideoId !== null && playingVideoId !== videoId) {
            videoRefs.get(playingVideoId)?.current?.pause();
        }
        setPlayingVideoId(videoId);

        isPlayPendingRef.current = true;
        try {
            await video.play();
        } catch (error) {
            // An AbortError just means a newer play()/pause() superseded this one.
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                isPauseDeferredRef.current = true;
            }
        } finally {
            isPlayPendingRef.current = false;
            if (isPauseDeferredRef.current) {
                isPauseDeferredRef.current = false;
                pauseNow();
            }
        }
    }, [pauseNow, videoId, videoRef]);

    const pause = useCallback(() => {
        // Pausing mid-play-promise makes the pending play() reject; defer until it settles instead.
        if (isPlayPendingRef.current) {
            isPauseDeferredRef.current = true;
            return;
        }
        pauseNow();
    }, [pauseNow]);

    const reset = useCallback(() => {
        const video = videoRef.current;
        if (video) video.currentTime = 0;
    }, [videoRef]);

    return { isPlaying, play, pause, reset };
}
